import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Pipe, EventEmitter, Output, ViewChildren, HostListener, Injectable, NgModule } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@eui/components/eui-button';
import { EuiButtonModule } from '@eui/components/eui-button';
import * as i3 from '@eui/components/eui-icon';
import { EuiIconModule } from '@eui/components/eui-icon';
import * as i5 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i1$1 from '@angular/common/http';
import { HttpEventType } from '@angular/common/http';
import { tap, filter, map } from 'rxjs/operators';
import { pipe } from 'rxjs';

class EuiFileUploadProgressComponent {
    constructor() {
        this.progress = 0;
    }
}
/** @nocollapse */ EuiFileUploadProgressComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadProgressComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiFileUploadProgressComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiFileUploadProgressComponent, selector: "eui-file-upload-progress", inputs: { progress: "progress" }, ngImport: i0, template: "<div *ngIf=\"progress > 0\" class=\"file-upload__progress-bar\">\n    <div class=\"progress-bar__container\">\n        <div class=\"progress-bar\" [style.width]=\"progress + '%'\"></div>\n    </div>\n    <div class=\"file-upload__progress-value\">{{ progress }}%</div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadProgressComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-file-upload-progress', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div *ngIf=\"progress > 0\" class=\"file-upload__progress-bar\">\n    <div class=\"progress-bar__container\">\n        <div class=\"progress-bar\" [style.width]=\"progress + '%'\"></div>\n    </div>\n    <div class=\"file-upload__progress-value\">{{ progress }}%</div>\n</div>\n" }]
        }], propDecorators: { progress: [{
                type: Input
            }] } });

class EuiFileSizePipe {
    constructor() {
        this.units = [
            'bytes',
            'KB',
            'MB',
            'GB',
            'TB',
            'PB',
        ];
    }
    transform(bytes = 0, precision = 2) {
        if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes)) {
            return '?';
        }
        let unit = 0;
        while (bytes >= 1024) {
            bytes /= 1024;
            unit++;
        }
        return bytes.toFixed(+precision) + ' ' + this.units[unit];
    }
}
/** @nocollapse */ EuiFileSizePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileSizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
/** @nocollapse */ EuiFileSizePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiFileSizePipe, name: "filesize" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileSizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'filesize',
                }]
        }] });

class EuiFilePreviewComponent {
    constructor(cd) {
        this.cd = cd;
        this.previewAsIconConfig = {
            avi: 'eui-icon-filetype-avi',
            html: 'eui-icon-filetype-html',
            htm: 'eui-icon-filetype-html',
            js: 'eui-icon-filetype-js',
            json: 'eui-icon-filetype-json',
            mp4: 'eui-icon-filetype-mp4',
            pdf: 'eui-icon-filetype-pdf',
            png: 'eui-icon-filetype-png',
            svg: 'eui-icon-filetype-svg',
            txt: 'eui-icon-filetype-txt',
            xml: 'eui-icon-filetype-xml',
            jpeg: 'eui-icon-filetype-jpg',
            jpg: 'eui-icon-filetype-jpg',
            zip: 'eui-icon-filetype-zip',
            doc: 'eui-icon-filetype-doc',
            docx: 'eui-icon-filetype-doc',
            xls: 'eui-icon-filetype-xls',
            xlsx: 'eui-icon-filetype-xls',
            ppt: 'eui-icon-filetype-ppt',
            csv: 'eui-icon-filetype-csv',
            rtf: 'eui-icon-filetype-rtf',
        };
        this.removeFromList = new EventEmitter();
        this._hasPreview = true;
        this._isFilenameDisplayed = true;
        this._isFilesizeDisplayed = true;
        this._isFiletypeDisplayed = true;
        this._hasPreviewAsImage = true;
        this._hasPreviewAsIcon = false;
    }
    get hasPreview() { return this._hasPreview; }
    set hasPreview(value) { this._hasPreview = coerceBooleanProperty(value); }
    get isFilenameDisplayed() { return this._isFilenameDisplayed; }
    set isFilenameDisplayed(value) { this._isFilenameDisplayed = coerceBooleanProperty(value); }
    get isFilesizeDisplayed() { return this._isFilesizeDisplayed; }
    set isFilesizeDisplayed(value) { this._isFilesizeDisplayed = coerceBooleanProperty(value); }
    get isFiletypeDisplayed() { return this._isFiletypeDisplayed; }
    set isFiletypeDisplayed(value) { this._isFiletypeDisplayed = coerceBooleanProperty(value); }
    get hasPreviewAsImage() { return this._hasPreviewAsImage; }
    set hasPreviewAsImage(value) { this._hasPreviewAsImage = coerceBooleanProperty(value); }
    get hasPreviewAsIcon() { return this._hasPreviewAsIcon; }
    set hasPreviewAsIcon(value) { this._hasPreviewAsIcon = coerceBooleanProperty(value); }
    ngOnInit() {
        if (this.hasPreviewAsIcon) {
            this.hasPreviewAsImage = false;
        }
        if (this.file instanceof Blob) {
            const reader = new FileReader();
            reader.readAsDataURL(this.file);
            reader.onload = (event) => {
                this.url = event.target.result;
                this.fileExtension = this.getFileExtension(this.file.name);
                this.icon = this.getIconForMime(this.fileExtension);
                this.previewData = this.getPreviewForMime(this.fileExtension);
                this.cd.detectChanges();
            };
        }
        else {
            this.url = this.file.url;
            this.fileExtension = this.getFileExtension(this.file.name);
            this.icon = this.getIconForMime(this.fileExtension);
            this.previewData = this.getPreviewForMime(this.fileExtension);
            this.cd.detectChanges();
        }
    }
    onRemoveFromList() {
        this.removeFromList.emit(this.index);
    }
    getFileExtension(fileName) {
        const ext = fileName.split('.');
        return ext[ext.length - 1];
    }
    getPreviewForMime(fileExtension) {
        const image = fileExtension === 'jpeg' || fileExtension === 'jpg' || fileExtension === 'png' ||
            fileExtension === 'gif' ? true : false;
        const display = fileExtension === 'jpeg' || fileExtension === 'jpg' || fileExtension === 'png' || fileExtension === 'gif' ?
            this.url :
            this.getIconForMime(fileExtension);
        return { image, display };
    }
    getIconForMime(fileExtension) {
        return !this.previewAsIconConfig[fileExtension] ? 'eui-icon-filetype-unknown' : this.previewAsIconConfig[fileExtension];
    }
}
/** @nocollapse */ EuiFilePreviewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFilePreviewComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiFilePreviewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiFilePreviewComponent, selector: "eui-file-preview", inputs: { file: "file", index: "index", previewAsIconConfig: "previewAsIconConfig", hasPreview: "hasPreview", isFilenameDisplayed: "isFilenameDisplayed", isFilesizeDisplayed: "isFilesizeDisplayed", isFiletypeDisplayed: "isFiletypeDisplayed", hasPreviewAsImage: "hasPreviewAsImage", hasPreviewAsIcon: "hasPreviewAsIcon" }, outputs: { removeFromList: "removeFromList" }, ngImport: i0, template: "<div class=\"file-upload__preview\">\n    <div class=\"file-upload__preview__image\" *ngIf=\"hasPreview && hasPreviewAsImage\">\n        <img [src]=\"previewData.display\" *ngIf=\"url && previewData.image\">\n        <i class=\"fa {{ previewData.display }}\" *ngIf=\"previewData && !previewData.image\"></i>\n    </div>\n    <div class=\"file-upload__preview__icon\" *ngIf=\"hasPreview && hasPreviewAsIcon\">\n        <span class=\"eui-icon eui-icon-color {{ icon }}\" euiIconColored euiSize5XL></span>\n    </div>\n    <div class=\"file-upload__preview__filename\" *ngIf=\"isFilenameDisplayed\">{{ file.name }}</div>\n    <div class=\"file-upload__preview__filesize\" *ngIf=\"isFilesizeDisplayed\">{{ file.size | filesize }}</div>\n    <div class=\"file-upload__preview__filetype\" *ngIf=\"isFiletypeDisplayed\">{{ file.type }}</div>\n    <div class=\"file-upload__preview__actions\">\n        <div class=\"file-upload__preview__actions-container\">\n            <button euiButton euiPrimary euiRounded euiIconButton euiSizeL euiBasicButton (click)=\"onRemoveFromList()\">\n                <eui-icon-svg icon=\"eui-close\"></eui-icon-svg>\n            </button>\n        </div>\n    </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.EuiButtonComponent, selector: "button[euiButton], a[euiButton]", inputs: ["e2eAttr", "id", "euiBasicButton", "euiButtonCall", "euiBlockButton", "isLoading", "euiIconButton", "euiLineWrap"], outputs: ["buttonClick"] }, { kind: "component", type: i3.EuiIconSvgComponent, selector: "eui-icon-svg, span[euiIconSvg], i[euiIconSvg]", inputs: ["icon", "size", "fillColor", "set", "ariaLabelledby", "role", "style", "iconUrl", "aria-label", "ariaHidden", "focusable", "isLoading"] }, { kind: "component", type: i3.EuiIconColorComponent, selector: "eui-icon-colored, span[euiIconColored], [euiIconColored]", inputs: ["iconClass"] }, { kind: "pipe", type: EuiFileSizePipe, name: "filesize" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFilePreviewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-file-preview', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div class=\"file-upload__preview\">\n    <div class=\"file-upload__preview__image\" *ngIf=\"hasPreview && hasPreviewAsImage\">\n        <img [src]=\"previewData.display\" *ngIf=\"url && previewData.image\">\n        <i class=\"fa {{ previewData.display }}\" *ngIf=\"previewData && !previewData.image\"></i>\n    </div>\n    <div class=\"file-upload__preview__icon\" *ngIf=\"hasPreview && hasPreviewAsIcon\">\n        <span class=\"eui-icon eui-icon-color {{ icon }}\" euiIconColored euiSize5XL></span>\n    </div>\n    <div class=\"file-upload__preview__filename\" *ngIf=\"isFilenameDisplayed\">{{ file.name }}</div>\n    <div class=\"file-upload__preview__filesize\" *ngIf=\"isFilesizeDisplayed\">{{ file.size | filesize }}</div>\n    <div class=\"file-upload__preview__filetype\" *ngIf=\"isFiletypeDisplayed\">{{ file.type }}</div>\n    <div class=\"file-upload__preview__actions\">\n        <div class=\"file-upload__preview__actions-container\">\n            <button euiButton euiPrimary euiRounded euiIconButton euiSizeL euiBasicButton (click)=\"onRemoveFromList()\">\n                <eui-icon-svg icon=\"eui-close\"></eui-icon-svg>\n            </button>\n        </div>\n    </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { file: [{
                type: Input
            }], index: [{
                type: Input
            }], previewAsIconConfig: [{
                type: Input
            }], removeFromList: [{
                type: Output
            }], hasPreview: [{
                type: Input
            }], isFilenameDisplayed: [{
                type: Input
            }], isFilesizeDisplayed: [{
                type: Input
            }], isFiletypeDisplayed: [{
                type: Input
            }], hasPreviewAsImage: [{
                type: Input
            }], hasPreviewAsIcon: [{
                type: Input
            }] } });

class EuiFileUploadComponent {
    constructor() {
        this.accept = '*';
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.drop = new EventEmitter();
        this.dropAreaClass = 'file-drop-area';
        this.files = [];
        this.filesTotalSize = 0;
        this.randomNumberId = Math.floor(Math.random() * 10000);
        this.uploadedFiles = [];
        this._isMultiple = true;
        this._hasProgressBar = true;
        this._hasPreview = true;
        this._hasPreviewAsImage = true;
        this._hasPreviewAsIcon = false;
        this._hasTotalSizeDisplayed = true;
        this._hasResetButton = true;
        this._hasDragArea = false;
        this.onChange = () => { };
    }
    get isMultiple() { return this._isMultiple; }
    set isMultiple(value) { this._isMultiple = coerceBooleanProperty(value); }
    get hasProgressBar() { return this._hasProgressBar; }
    set hasProgressBar(value) { this._hasProgressBar = coerceBooleanProperty(value); }
    get hasPreview() { return this._hasPreview; }
    set hasPreview(value) { this._hasPreview = coerceBooleanProperty(value); }
    get hasPreviewAsImage() { return this._hasPreviewAsImage; }
    set hasPreviewAsImage(value) { this._hasPreviewAsImage = coerceBooleanProperty(value); }
    get hasPreviewAsIcon() { return this._hasPreviewAsIcon; }
    set hasPreviewAsIcon(value) { this._hasPreviewAsIcon = coerceBooleanProperty(value); }
    get hasTotalSizeDisplayed() { return this._hasTotalSizeDisplayed; }
    set hasTotalSizeDisplayed(value) { this._hasTotalSizeDisplayed = coerceBooleanProperty(value); }
    get hasResetButton() { return this._hasResetButton; }
    set hasResetButton(value) { this._hasResetButton = coerceBooleanProperty(value); }
    get hasDragArea() { return this._hasDragArea; }
    set hasDragArea(value) { this._hasDragArea = coerceBooleanProperty(value); }
    emitFiles(fileList) {
        for (const property in fileList) {
            if (fileList[property] && typeof fileList[property] === 'object') {
                const fileName = fileList[property].name.split('.');
                const fileExtension = fileName[fileName.length - 1];
                if (this.accept.indexOf(fileExtension) !== -1 || this.accept === '*') {
                    this.files.push(fileList[property]);
                }
            }
        }
        this.getTotalSize();
        this.uploadedFiles ? this.onChange([...this.uploadedFiles, ...this.files]) : this.onChange(this.files);
        if (this.fileInputs) {
            this.fileInputs.first.nativeElement.value = '';
            this.fileInputs.last.nativeElement.value = '';
        }
        this.dropAreaClass = 'file-drop-area';
        this.drop.emit();
    }
    ngOnInit() {
        if (this.hasPreviewAsIcon) {
            this.hasPreviewAsImage = false;
        }
    }
    get showResetBtn() {
        return this.hasResetButton && (this.files.length > 0 || this.uploadedFiles.length > 0);
    }
    onDragEnter() {
        this.dropAreaClass = 'file-drop-area-active';
    }
    onDragLeave() {
        this.dropAreaClass = 'file-drop-area';
    }
    removeFromList(index) {
        this.files = this.files.filter((f, i) => i !== index);
        if (this.files.length === 0) {
            this.progress = 0;
        }
        this.getTotalSize();
        this.onChange([...this.uploadedFiles, ...this.files]);
    }
    removeFromUploadedList(index) {
        this.uploadedFiles = this.uploadedFiles.filter((f, i) => i !== index);
        if (this.files.length === 0) {
            this.progress = 0;
        }
        this.getTotalSize();
        this.onChange([...this.uploadedFiles, ...this.files]);
    }
    resetList() {
        this.files = [];
        this.uploadedFiles = [];
        this.getTotalSize();
        this.progress = 0;
        this.onChange([...this.uploadedFiles, ...this.files]);
    }
    openBrowseWindow() {
        this.fileInputs.first.nativeElement.click();
    }
    writeValue(value) {
        this.uploadedFiles = value || [];
        this.files = [];
        this.getTotalSize();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
    }
    getTotalSize() {
        this.filesTotalSize = 0;
        this.files.forEach((f) => {
            this.filesTotalSize += f.size;
        });
    }
}
/** @nocollapse */ EuiFileUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiFileUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiFileUploadComponent, selector: "eui-file-upload", inputs: { progress: "progress", accept: "accept", isMultiple: "isMultiple", hasProgressBar: "hasProgressBar", hasPreview: "hasPreview", hasPreviewAsImage: "hasPreviewAsImage", hasPreviewAsIcon: "hasPreviewAsIcon", hasTotalSizeDisplayed: "hasTotalSizeDisplayed", hasResetButton: "hasResetButton", hasDragArea: "hasDragArea" }, outputs: { drop: "drop" }, host: { listeners: { "change": "emitFiles($event.target.files)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: EuiFileUploadComponent,
            multi: true,
        },
    ], viewQueries: [{ propertyName: "fileInputs", predicate: ["input"], descendants: true }], ngImport: i0, template: "<div class=\"file-upload\">\n    <div [class]=\"dropAreaClass\" *ngIf=\"hasDragArea\">\n        <span class=\"fake-btn\">{{ 'eui.euifileupload.CHOOSE-FILE' | translate }}</span>\n        <span class=\"file-msg\">{{ 'eui.euifileupload.DRAG-AND-DROP-FILE-HERE' | translate }}</span>\n        <input\n            #input\n            class=\"file-input\"\n            attr.aria-label= \"{{'eui.euifileupload.CHOOSE-FILE' | translate }} 'or' {{ 'eui.euifileupload.DRAG-AND-DROP-FILE-HERE' | translate }}\"\n            type=\"file\"\n            accept=\"{{ accept }}\"\n            [multiple]=\"isMultiple\"\n            (dragenter)=\"onDragEnter()\"\n            (dragleave)=\"onDragLeave()\">\n    </div>\n    <div class=\"simple-input-file eui-u-mb-xs\" *ngIf=\"!hasDragArea\">\n        <label for=\"file-{{ randomNumberId }}\">\n            <button euiButton (click)=\"openBrowseWindow()\">{{ 'eui.euifileupload.CHOOSE-FILE' | translate }}</button>\n        </label>\n\n        <input #input *ngIf=\"isMultiple\" aria-label=\"Choose Multiple Files\" class=\"file-input\" id=\"file-{{ randomNumberId }}\" type=\"file\" accept=\"{{ accept }}\" multiple>\n        <input #input *ngIf=\"!isMultiple\" aria-label=\"Choose a single file\" class=\"file-input\" id=\"file-{{ randomNumberId }}\" type=\"file\" accept=\"{{ accept }}\">\n    </div>\n    <eui-file-upload-progress *ngIf=\"hasProgressBar\" [progress]=\"progress\"></eui-file-upload-progress>\n\n    <div *ngFor=\"let uploadedFile of uploadedFiles; let i = index\">\n        <eui-file-preview\n            [index]=\"i\"\n            [file]=\"uploadedFile\"\n            [hasPreview]=\"hasPreview\"\n            [hasPreviewAsImage]=\"hasPreviewAsImage\"\n            [hasPreviewAsIcon]=\"hasPreviewAsIcon\"\n            (removeFromList)=\"removeFromUploadedList($event)\">\n        </eui-file-preview>\n    </div>\n\n    <div *ngFor=\"let file of files; let i = index\">\n        <eui-file-preview\n            [index]=\"i\"\n            [file]=\"file\"\n            [hasPreview]=\"hasPreview\"\n            [hasPreviewAsImage]=\"hasPreviewAsImage\"\n            [hasPreviewAsIcon]=\"hasPreviewAsIcon\"\n            (removeFromList)=\"removeFromList($event)\">\n        </eui-file-preview>\n    </div>\n\n    <div class=\"file-upload__total-size\" *ngIf=\"filesTotalSize && hasTotalSizeDisplayed\">\n        {{ filesTotalSize | filesize }} {{ 'eui.euifileupload.TOTAL-SIZE' | translate }}\n    </div>\n</div>\n<ng-content></ng-content>\n<button euiButton class=\"file-upload__reset-button\" type=\"button\" (click)=\"resetList()\" *ngIf=\"showResetBtn\">{{ 'eui.euifileupload.RESET-LIST' | translate }}</button>\n", styles: [".file-upload .file-upload__total-size{font-weight:700;margin-bottom:1rem}.file-upload .file-drop-area{outline:1px dashed var(--eui-base-color-primary-75);border-radius:3px;margin:0 0 10px;padding:30px;position:relative;transition:.2s}.file-upload .file-drop-area-active{background-color:var(--eui-base-color-primary-5);outline:3px dashed var(--eui-base-color-primary-75);border-radius:3px;margin:0 0 10px;padding:30px;position:relative;transition:.2s}.file-upload .fake-btn{background-color:#fff;border:1px solid #9e9ec4;border-radius:3px;margin-right:8px;padding:8px 15px}.file-upload .file-input{cursor:pointer;height:100%;left:0;opacity:0;position:absolute;top:0;width:100%}.file-upload .simple-input-file .file-input{display:none}.file-upload .file-upload__preview{align-items:center;display:flex;flex-wrap:wrap;justify-content:space-between;margin:5px 0}.file-upload .file-upload__preview .file-upload__preview__image img{width:180px}.file-upload .file-upload__preview .file-upload__preview__filename{width:50%}.file-upload .progress-bar__container{margin-bottom:var(--eui-base-spacing-2xs)}.file-upload .progress-bar__container .progress-bar{background-color:var(--eui-base-color-primary-100);height:var(--eui-base-spacing-2xs)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.EuiButtonComponent, selector: "button[euiButton], a[euiButton]", inputs: ["e2eAttr", "id", "euiBasicButton", "euiButtonCall", "euiBlockButton", "isLoading", "euiIconButton", "euiLineWrap"], outputs: ["buttonClick"] }, { kind: "component", type: EuiFileUploadProgressComponent, selector: "eui-file-upload-progress", inputs: ["progress"] }, { kind: "component", type: EuiFilePreviewComponent, selector: "eui-file-preview", inputs: ["file", "index", "previewAsIconConfig", "hasPreview", "isFilenameDisplayed", "isFilesizeDisplayed", "isFiletypeDisplayed", "hasPreviewAsImage", "hasPreviewAsIcon"], outputs: ["removeFromList"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }, { kind: "pipe", type: EuiFileSizePipe, name: "filesize" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-file-upload', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: EuiFileUploadComponent,
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<div class=\"file-upload\">\n    <div [class]=\"dropAreaClass\" *ngIf=\"hasDragArea\">\n        <span class=\"fake-btn\">{{ 'eui.euifileupload.CHOOSE-FILE' | translate }}</span>\n        <span class=\"file-msg\">{{ 'eui.euifileupload.DRAG-AND-DROP-FILE-HERE' | translate }}</span>\n        <input\n            #input\n            class=\"file-input\"\n            attr.aria-label= \"{{'eui.euifileupload.CHOOSE-FILE' | translate }} 'or' {{ 'eui.euifileupload.DRAG-AND-DROP-FILE-HERE' | translate }}\"\n            type=\"file\"\n            accept=\"{{ accept }}\"\n            [multiple]=\"isMultiple\"\n            (dragenter)=\"onDragEnter()\"\n            (dragleave)=\"onDragLeave()\">\n    </div>\n    <div class=\"simple-input-file eui-u-mb-xs\" *ngIf=\"!hasDragArea\">\n        <label for=\"file-{{ randomNumberId }}\">\n            <button euiButton (click)=\"openBrowseWindow()\">{{ 'eui.euifileupload.CHOOSE-FILE' | translate }}</button>\n        </label>\n\n        <input #input *ngIf=\"isMultiple\" aria-label=\"Choose Multiple Files\" class=\"file-input\" id=\"file-{{ randomNumberId }}\" type=\"file\" accept=\"{{ accept }}\" multiple>\n        <input #input *ngIf=\"!isMultiple\" aria-label=\"Choose a single file\" class=\"file-input\" id=\"file-{{ randomNumberId }}\" type=\"file\" accept=\"{{ accept }}\">\n    </div>\n    <eui-file-upload-progress *ngIf=\"hasProgressBar\" [progress]=\"progress\"></eui-file-upload-progress>\n\n    <div *ngFor=\"let uploadedFile of uploadedFiles; let i = index\">\n        <eui-file-preview\n            [index]=\"i\"\n            [file]=\"uploadedFile\"\n            [hasPreview]=\"hasPreview\"\n            [hasPreviewAsImage]=\"hasPreviewAsImage\"\n            [hasPreviewAsIcon]=\"hasPreviewAsIcon\"\n            (removeFromList)=\"removeFromUploadedList($event)\">\n        </eui-file-preview>\n    </div>\n\n    <div *ngFor=\"let file of files; let i = index\">\n        <eui-file-preview\n            [index]=\"i\"\n            [file]=\"file\"\n            [hasPreview]=\"hasPreview\"\n            [hasPreviewAsImage]=\"hasPreviewAsImage\"\n            [hasPreviewAsIcon]=\"hasPreviewAsIcon\"\n            (removeFromList)=\"removeFromList($event)\">\n        </eui-file-preview>\n    </div>\n\n    <div class=\"file-upload__total-size\" *ngIf=\"filesTotalSize && hasTotalSizeDisplayed\">\n        {{ filesTotalSize | filesize }} {{ 'eui.euifileupload.TOTAL-SIZE' | translate }}\n    </div>\n</div>\n<ng-content></ng-content>\n<button euiButton class=\"file-upload__reset-button\" type=\"button\" (click)=\"resetList()\" *ngIf=\"showResetBtn\">{{ 'eui.euifileupload.RESET-LIST' | translate }}</button>\n", styles: [".file-upload .file-upload__total-size{font-weight:700;margin-bottom:1rem}.file-upload .file-drop-area{outline:1px dashed var(--eui-base-color-primary-75);border-radius:3px;margin:0 0 10px;padding:30px;position:relative;transition:.2s}.file-upload .file-drop-area-active{background-color:var(--eui-base-color-primary-5);outline:3px dashed var(--eui-base-color-primary-75);border-radius:3px;margin:0 0 10px;padding:30px;position:relative;transition:.2s}.file-upload .fake-btn{background-color:#fff;border:1px solid #9e9ec4;border-radius:3px;margin-right:8px;padding:8px 15px}.file-upload .file-input{cursor:pointer;height:100%;left:0;opacity:0;position:absolute;top:0;width:100%}.file-upload .simple-input-file .file-input{display:none}.file-upload .file-upload__preview{align-items:center;display:flex;flex-wrap:wrap;justify-content:space-between;margin:5px 0}.file-upload .file-upload__preview .file-upload__preview__image img{width:180px}.file-upload .file-upload__preview .file-upload__preview__filename{width:50%}.file-upload .progress-bar__container{margin-bottom:var(--eui-base-spacing-2xs)}.file-upload .progress-bar__container .progress-bar{background-color:var(--eui-base-color-primary-100);height:var(--eui-base-spacing-2xs)}\n"] }]
        }], propDecorators: { progress: [{
                type: Input
            }], accept: [{
                type: Input
            }], drop: [{
                type: Output
            }], fileInputs: [{
                type: ViewChildren,
                args: ['input']
            }], isMultiple: [{
                type: Input
            }], hasProgressBar: [{
                type: Input
            }], hasPreview: [{
                type: Input
            }], hasPreviewAsImage: [{
                type: Input
            }], hasPreviewAsIcon: [{
                type: Input
            }], hasTotalSizeDisplayed: [{
                type: Input
            }], hasResetButton: [{
                type: Input
            }], hasDragArea: [{
                type: Input
            }], emitFiles: [{
                type: HostListener,
                args: ['change', ['$event.target.files']]
            }] } });

class EuiFileUploadUtilsService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /**
     * @param data Data to send to backend
     * @param APIEndPoint Endpoint URL
     *
     * @description
     * Method that will format data in `FormData` and send them to backend.
     *
     * @usageNotes
     * Call the method and log the response in the console.
     *
     * ```typescript
     * this.euiFileUploadUtilsService.sendData(this.form.value, 'http://localhost:3000/api/fake-api').pipe(
     *     this.euiFileUploadUtilsService.uploadProgress((progress) => {
     *         console.log(progress);
     *     }),
     *     this.euiFileUploadUtilsService.toResponseBody(),
     * ).subscribe((response) => {
     *      console.log(response);
     * });
     * ```
     *
     * @returns The backend response.
     */
    sendData(data, APIEndPoint) {
        return this.httpClient.post(APIEndPoint, this.toFormData(data), {
            reportProgress: true,
            observe: 'events',
        });
    }
    /**
     * @param cb A method to execute on each progress step.
     *
     * @description
     * Method that will return the upload progress.
     *
     * @usageNotes
     * Call the method and log the progress in the console.
     *
     * ```typescript
     * this.euiFileUploadUtilsService.sendData(this.form.value, 'http://localhost:3000/api/fake-api').pipe(
     *     this.euiFileUploadUtilsService.uploadProgress((progress) => {
     *         console.log(progress);
     *     }),
     *     this.euiFileUploadUtilsService.toResponseBody(),
     * ).subscribe((response) => {
     *      console.log(response);
     * });
     * ```
     *
     * @returns A number between 0 and 100.
     */
    uploadProgress(cb) {
        return tap((event) => {
            if (event.type === HttpEventType.UploadProgress) {
                cb(Math.round((100 * event.loaded) / event.total));
            }
        });
    }
    /**
     * @description
     * Method that will return only the body of the backend response.
     *
     * @usageNotes
     * Call the method and log the response in the console.
     *
     * ```typescript
     * this.euiFileUploadUtilsService.sendData(this.form.value, 'http://localhost:3000/api/fake-api').pipe(
     *     this.euiFileUploadUtilsService.uploadProgress((progress) => {
     *         console.log(progress);
     *     }),
     *     this.euiFileUploadUtilsService.toResponseBody(),
     * ).subscribe((response) => {
     *      console.log(response);
     * });
     * ```
     *
     * @returns The backend response.
     */
    toResponseBody() {
        return pipe(filter((event) => event.type === HttpEventType.Response), map((res) => res.body));
    }
    toFormData(formValue) {
        const formData = new FormData();
        for (const key of Object.keys(formValue)) {
            if (Array.isArray(formValue[key])) {
                let fileIndex = 0;
                formValue[key].forEach((value) => {
                    if (value instanceof File) {
                        formData.append(key + fileIndex, value);
                        fileIndex++;
                    }
                });
                const a = [];
                formValue[key].forEach((value, index) => {
                    a.push(value);
                });
                formData.append(key, JSON.stringify(a));
            }
            else {
                if (typeof formValue[key] === 'string') {
                    formData.set(key, formValue[key]);
                }
                else {
                    formData.set(key, JSON.stringify(formValue[key]));
                }
            }
        }
        return formData;
    }
}
/** @nocollapse */ EuiFileUploadUtilsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadUtilsService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ EuiFileUploadUtilsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadUtilsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadUtilsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

class EuiUploadedFileComponent {
    constructor(cd) {
        this.cd = cd;
        this.previewAsIconConfig = {
            'image/jpeg': 'fa-picture-o',
            'image/gif': 'fa-picture-o',
            'image/png': 'fa-picture-o',
            'application/msword': 'fa-file-word-o',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'fa-file-word-o',
            'application/vnd.ms-excel': 'fa-file-excel-o',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'fa-file-excel-o',
            'application/pdf': 'fa-file-pdf-o',
        };
        this.removeFromList = new EventEmitter();
        this._hasPreview = true;
        this._isFilenameDisplayed = true;
        this._isFilesizeDisplayed = true;
        this._isFiletypeDisplayed = true;
        this._hasPreviewAsImage = true;
        this._hasPreviewAsIcon = false;
    }
    get hasPreview() { return this._hasPreview; }
    set hasPreview(value) { this._hasPreview = coerceBooleanProperty(value); }
    get isFilenameDisplayed() { return this._isFilenameDisplayed; }
    set isFilenameDisplayed(value) { this._isFilenameDisplayed = coerceBooleanProperty(value); }
    get isFilesizeDisplayed() { return this._isFilesizeDisplayed; }
    set isFilesizeDisplayed(value) { this._isFilesizeDisplayed = coerceBooleanProperty(value); }
    get isFiletypeDisplayed() { return this._isFiletypeDisplayed; }
    set isFiletypeDisplayed(value) { this._isFiletypeDisplayed = coerceBooleanProperty(value); }
    get hasPreviewAsImage() { return this._hasPreviewAsImage; }
    set hasPreviewAsImage(value) { this._hasPreviewAsImage = coerceBooleanProperty(value); }
    get hasPreviewAsIcon() { return this._hasPreviewAsIcon; }
    set hasPreviewAsIcon(value) { this._hasPreviewAsIcon = coerceBooleanProperty(value); }
    ngOnInit() {
        if (this.hasPreviewAsIcon) {
            this.hasPreviewAsImage = false;
        }
        const reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = (event) => {
            this.url = event.target.result;
            this.icon = this.getIconForMime(this.file.type);
            this.previewData = this.getPreviewForMime(this.file.type);
            this.cd.detectChanges();
        };
    }
    onRemoveFromList() {
        this.removeFromList.emit(this.index);
    }
    getPreviewForMime(mime) {
        const image = mime === 'image/jpeg' || mime === 'image/gif' || mime === 'image/png' ? true : false;
        const display = mime === 'image/jpeg' || mime === 'image/gif' || mime === 'image/png' ?
            this.url :
            this.getIconForMime(mime);
        return { image, display };
    }
    getIconForMime(mime) {
        return !this.previewAsIconConfig[mime] ? 'fa-file-o' : this.previewAsIconConfig[mime];
    }
}
/** @nocollapse */ EuiUploadedFileComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiUploadedFileComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiUploadedFileComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiUploadedFileComponent, selector: "eui-uploaded-file", inputs: { file: "file", index: "index", previewAsIconConfig: "previewAsIconConfig", hasPreview: "hasPreview", isFilenameDisplayed: "isFilenameDisplayed", isFilesizeDisplayed: "isFilesizeDisplayed", isFiletypeDisplayed: "isFiletypeDisplayed", hasPreviewAsImage: "hasPreviewAsImage", hasPreviewAsIcon: "hasPreviewAsIcon" }, outputs: { removeFromList: "removeFromList" }, ngImport: i0, template: "<div class=\"file-upload__preview\">\n    <div class=\"file-upload__preview__image\" *ngIf=\"hasPreview && hasPreviewAsImage\">\n        <img [src]=\"previewData.display\" *ngIf=\"url && previewData.image\">\n        <i class=\"fa {{ previewData.display }}\" *ngIf=\"previewData && !previewData.image\"></i>\n    </div>\n    <div class=\"file-upload__preview__icon\" *ngIf=\"hasPreview && hasPreviewAsIcon\">\n        <i class=\"fa {{ icon }}\"></i>\n    </div>\n    <div class=\"file-upload__preview__filename\" *ngIf=\"isFilenameDisplayed\">{{ file.name }}</div>\n    <div class=\"file-upload__preview__filesize\" *ngIf=\"isFilesizeDisplayed\">{{ file.size | filesize }}</div>\n    <div class=\"file-upload__preview__filetype\" *ngIf=\"isFiletypeDisplayed\">{{ file.type }}</div>\n    <div class=\"file-upload__preview__actions\">\n        <div class=\"file-upload__preview__actions-container\">\n            <button euiButton euiRounded euiIconButton euiSizeL euiBasicButton (click)=\"onRemoveFromList()\">\n                <eui-icon-svg icon=\"eui-close\"></eui-icon-svg>\n            </button>\n        </div>\n    </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.EuiButtonComponent, selector: "button[euiButton], a[euiButton]", inputs: ["e2eAttr", "id", "euiBasicButton", "euiButtonCall", "euiBlockButton", "isLoading", "euiIconButton", "euiLineWrap"], outputs: ["buttonClick"] }, { kind: "component", type: i3.EuiIconSvgComponent, selector: "eui-icon-svg, span[euiIconSvg], i[euiIconSvg]", inputs: ["icon", "size", "fillColor", "set", "ariaLabelledby", "role", "style", "iconUrl", "aria-label", "ariaHidden", "focusable", "isLoading"] }, { kind: "pipe", type: EuiFileSizePipe, name: "filesize" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiUploadedFileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-uploaded-file', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div class=\"file-upload__preview\">\n    <div class=\"file-upload__preview__image\" *ngIf=\"hasPreview && hasPreviewAsImage\">\n        <img [src]=\"previewData.display\" *ngIf=\"url && previewData.image\">\n        <i class=\"fa {{ previewData.display }}\" *ngIf=\"previewData && !previewData.image\"></i>\n    </div>\n    <div class=\"file-upload__preview__icon\" *ngIf=\"hasPreview && hasPreviewAsIcon\">\n        <i class=\"fa {{ icon }}\"></i>\n    </div>\n    <div class=\"file-upload__preview__filename\" *ngIf=\"isFilenameDisplayed\">{{ file.name }}</div>\n    <div class=\"file-upload__preview__filesize\" *ngIf=\"isFilesizeDisplayed\">{{ file.size | filesize }}</div>\n    <div class=\"file-upload__preview__filetype\" *ngIf=\"isFiletypeDisplayed\">{{ file.type }}</div>\n    <div class=\"file-upload__preview__actions\">\n        <div class=\"file-upload__preview__actions-container\">\n            <button euiButton euiRounded euiIconButton euiSizeL euiBasicButton (click)=\"onRemoveFromList()\">\n                <eui-icon-svg icon=\"eui-close\"></eui-icon-svg>\n            </button>\n        </div>\n    </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { file: [{
                type: Input
            }], index: [{
                type: Input
            }], previewAsIconConfig: [{
                type: Input
            }], removeFromList: [{
                type: Output
            }], hasPreview: [{
                type: Input
            }], isFilenameDisplayed: [{
                type: Input
            }], isFilesizeDisplayed: [{
                type: Input
            }], isFiletypeDisplayed: [{
                type: Input
            }], hasPreviewAsImage: [{
                type: Input
            }], hasPreviewAsIcon: [{
                type: Input
            }] } });

class EuiFileUploadModule {
}
/** @nocollapse */ EuiFileUploadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiFileUploadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadModule, declarations: [EuiFileUploadComponent,
        EuiFileUploadProgressComponent,
        EuiFilePreviewComponent,
        EuiFileSizePipe,
        EuiUploadedFileComponent], imports: [CommonModule,
        EuiButtonModule,
        EuiIconModule,
        TranslateModule], exports: [EuiFileUploadComponent] });
/** @nocollapse */ EuiFileUploadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadModule, providers: [
        EuiFileUploadUtilsService,
    ], imports: [CommonModule,
        EuiButtonModule,
        EuiIconModule,
        TranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiFileUploadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        EuiButtonModule,
                        EuiIconModule,
                        TranslateModule,
                    ],
                    declarations: [
                        EuiFileUploadComponent,
                        EuiFileUploadProgressComponent,
                        EuiFilePreviewComponent,
                        EuiFileSizePipe,
                        EuiUploadedFileComponent,
                    ],
                    providers: [
                        EuiFileUploadUtilsService,
                    ],
                    exports: [
                        EuiFileUploadComponent,
                    ],
                }]
        }] });

/**
 * @description
 * Validator that requires the maximum length of files that can be uploaded.
 *
 * @usageNotes
 * Validate that the user selected maximum 3 files:
 *
 * ```typescript
 * const fileUpload = new FormControl(null, maxFilesValidator(3));
 * console.log(fileUpload.errors); // { maxFiles: 3 }
 * ```
 *
 * @returns A validator function that returns an error map with the
 * `maxFiles` if the validation check fails, otherwise `null`.
 */
const maxFilesValidator = (maxFiles) => (control) => control.value && control.value.length > maxFiles ? { maxFiles } : null;
/**
 * @description
 * Validator that requires the maximum size (in bytes) of files that can be uploaded.
 *
 * @usageNotes
 * Validate that the user doesn't select a file have a size up to 300Kb:
 *
 * ```typescript
 * const fileUpload = new FormControl(null, maxSizeValidator(300000));
 * console.log(fileUpload.errors); // { maxSize: 300000 }
 * ```
 *
 * @returns A validator function that returns an error map with the
 * `maxSize` if the validation check fails, otherwise `null`.
 */
const maxSizeValidator = (maxSize) => (control) => {
    let totalSize = 0;
    if (control.value) {
        control.value.forEach((file) => {
            totalSize += file.size;
        });
    }
    return control.value && totalSize > (maxSize * 1.024) ? { maxSize } : null;
};
/**
 * @description
 * Validator that requires an array of mime type that can be uploaded.
 *
 * @usageNotes
 * Validate that the user doesn't select a file having another extension than .jpg or .jpeg:
 *
 * ```typescript
 * const fileUpload = new FormControl(null, mimeTypeExtensionValidator(['image/jpeg'])),
 * console.log(fileUpload.errors); // { invalidFileExtension: ['image/jpeg'] }
 * ```
 *
 * @returns A validator function that returns an error map with the
 * `invalidFileExtension` if the validation check fails, otherwise `null`.
 */
const mimeTypeExtensionValidator = (mimeTypes) => (control) => {
    const invalidFileExtension = [];
    if (control.value) {
        control.value.forEach((file) => {
            if (mimeTypes.indexOf(file.type) === -1) {
                invalidFileExtension.push(file.name);
            }
        });
    }
    return control.value && invalidFileExtension.length > 0 ? { invalidFileExtension } : null;
};

class EuiUploadedFile {
    constructor(values) {
        Object.assign(this, values);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { EuiFilePreviewComponent, EuiFileUploadComponent, EuiFileUploadModule, EuiFileUploadProgressComponent, EuiFileUploadUtilsService, EuiUploadedFile, maxFilesValidator, maxSizeValidator, mimeTypeExtensionValidator };
